import { Answer, ChosenAnswer, IAdobeInteraction, Question } from "@abbvie-epi/common/models";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import gsap from "gsap";
import { AnalyticsHelperService } from "../../services/analytics-helper.service";
import { QuestionsService } from "../../services/questions.service";
import { ResultsService } from "../../services/results.service";

@Component({
    selector: "app-screening-tool-page",
    templateUrl: "./screening-tool-page.component.html",
    styleUrls: ["./screening-tool-page.component.scss"]
})
export class ScreeningToolPageComponent implements OnInit {
    private data: any;
    public questionId: string = "epi-01"; // epi-01 is the first question
    public currentQuestion: Question;
    public unknown?: string;
    public seeProgressCode: boolean = false;

    public snackbarShowing: boolean = false;

    public answer: string = "";

    public CloseType = CloseType;

    private firstInteraction: boolean = true;

    @ViewChild("snackbar") snackbar: ElementRef;

    constructor(
        private router: Router,
        private questionsService: QuestionsService,
        private resultService: ResultsService,
        private activatedRoute: ActivatedRoute,
        private analyticsHelper: AnalyticsHelperService
    ) {}

    ngOnInit() {
        this.getRouteFragment();
        this.getQuestion();

        this.analyticsHelper.trackPageLoad("screening-tool");
    }

    private getRouteFragment() {
        this.activatedRoute.fragment.subscribe((value) => {
            if (value) {
                this.questionId = value;
            }
        });
    }

    private getQuestion() {
        this.questionsService.getData().subscribe((data: any) => {
            this.data = data;
            this.currentQuestion = data[this.questionId];
        });
    }

    questionToggle(answer: string) {
        this.answer = answer;

        this.trackFormInteraction(answer);
    }

    public async answerQuestion(): Promise<void> {
        if (this.answer === "yes" || this.answer === "no" || this.answer === "unknown") {
            const answer: Answer = this.currentQuestion[this.answer];

            if (answer.type === "end") {
                this.resultService.setResultId(answer.id);

                await this.trackFormSubmission(answer.id);

                this.router.navigate(["/screening-tool-landing"], { fragment: "start-screening" });
            } else if (answer.type === "question") {
                this.trackFormInteraction("Continue");

                this.questionId = answer.id;
                this.currentQuestion = this.data[this.questionId];
            }

            this.answer = "";
            this.seeProgressCode = false;
        }
    }

    public toggleProgressCode() {
        this.seeProgressCode = !this.seeProgressCode;

        const analyticsOverride: IAdobeInteraction = {
            link: {
                name: `Progress Code Popup ${this.seeProgressCode ? "Open" : "Close"}: ${this.questionId}`,
                title: this.seeProgressCode ? "Missing these data?" : "Close"
            },
            component: {
                title: "Missing these data?"
            }
        };

        this.trackCtaInteraction(analyticsOverride);
    }

    public copyProgressCode() {
        navigator.clipboard.writeText(this.questionId).catch(() => {
            console.error("Unable to copy text");
        });

        const analyticsOverride: IAdobeInteraction = {
            link: {
                name: `Progress Code Copy: ${this.questionId}`,
                title: "Copy progress code"
            },
            component: {
                title: "Missing these data?"
            }
        };

        this.trackCtaInteraction(analyticsOverride);

        // animated snackbar
        if (!this.snackbarShowing) {
            this.snackbarShowing = true;

            gsap.to(this.snackbar.nativeElement, { visibility: "visible", opacity: "100%", duration: 0.25 });
            gsap.fromTo(this.snackbar.nativeElement, { opacity: "100%", duration: 1.5 }, { opacity: "0%", delay: 4 });
            gsap.to(this.snackbar.nativeElement, { visibility: "hidden", delay: 5.5 });

            setTimeout(() => {
                this.snackbarShowing = false;
            }, 5500);
        }
    }

    public routeTo(route: string, closeType: CloseType) {
        const analyticsOverride: IAdobeInteraction = {
            link: {
                name: `Exit the screening tool: ${this.currentQuestion.question}`,
                title: closeType === CloseType.SiteLogo ? "Site Logo" : "Exit",
                url: route
            }
        };

        this.trackCtaInteraction(analyticsOverride);

        this.router.navigate([route]);
    }

    private async trackFormSubmission(resultId: string): Promise<void> {
        const results = await this.resultService.getResult();

        if (!results) {
            return;
        }

        const questionAndResponses = results.chosenAnswers;

        const formattedQuestionAndResponses = this.formatQuestionAndResponses(questionAndResponses);

        const analyticsOverride: IAdobeInteraction = {
            link: {
                name: `Final EPI: ${resultId}`,
                title: "Result",
                type: "form-submission",
                url: "screening-tool-landing#start-screening"
            },
            mva: {
                tier: "primary",
                type: "form",
                category: "other",
                name: "The Screen for EPI in CP tool"
            },
            form: {
                stepName: "submit",
                fieldName: "Final EPI",
                fieldValue: resultId,
                fieldValues: formattedQuestionAndResponses
            }
        };

        this.analyticsHelper.trackInteraction("screening-tool-base-form-interaction", analyticsOverride);
    }

    private trackFormInteraction(userAction: string): void {
        const action = userAction[0].toUpperCase() + userAction.slice(1).toLowerCase();

        const analyticsOverride: IAdobeInteraction = {
            link: {
                name: `${this.currentQuestion.question}: ${action}`,
                title: action
            },
            form: {
                fieldName: this.currentQuestion.question,
                fieldValue: action
            }
        };

        if (this.firstInteraction) {
            analyticsOverride.link!.type = "form-initiation";
            analyticsOverride.form!.stepName = "initiation";

            this.firstInteraction = false;
        }

        this.analyticsHelper.trackInteraction("screening-tool-base-form-interaction", analyticsOverride);
    }

    private trackCtaInteraction(analyticsOverride: IAdobeInteraction): void {
        this.analyticsHelper.trackInteraction("screening-tool-base-cta-interaction", analyticsOverride);
    }

    private formatQuestionAndResponses(chosenAnswers: ChosenAnswer[]): string {
        return chosenAnswers
            .map(({ question, answer }) => {
                return `${question}:${answer}`;
            })
            .concat("Final EPI:" + this.questionId)
            .join("|");
    }
}

export enum CloseType {
    SiteLogo,
    Button
}
